import React, { FC, useId } from "react"

import {
  Autocomplete,
  Box,
  useTheme,
  debounce,
  Typography,
  Stack,
  Button,
  Tooltip,
} from "@mui/material"
import { ReactComponent as Arrow } from "assets/common/svg/arrowBack.svg"
import { useAppDispatch } from "hooks/store"
import { useTranslation } from "react-i18next"
import { setActivePageSignUp } from "store/auth/auth.slice"
import { DropdownStyles } from "styles/common/autocomplete"
import { unstyledButtonSx } from "styles/common/button"
import { IAutocompleteProps } from "ts/interfaces/Input"

import { ErrorStyle, TextFieldStyle } from "../FindSchoolForm.style"
import { IProps } from "./AutocompleteInput.types"

const AutocompleteInput: FC<IProps> = (props) => {
  const {
    userPost,
    data,
    state,
    errorInfo,
    setUserPost,
    setSchool,
    isLoading,
    setState,
    onClickNextSkip,
    onClickPrevSkip,
    skip,
    setSkip,
  } = props
  const dispatch = useAppDispatch()
  const theme = useTheme()
  const { t } = useTranslation()
  const errorId = useId()

  const isTextTruncated = (text: string, maxWidth = 215) => {
    const canvas = document.createElement("canvas")
    const context = canvas.getContext("2d")
    if (context) {
      context.font = "14px Roboto"
      const textWidth = context.measureText(text).width
      return textWidth > maxWidth
    }
    return text.length > 40
  }

  return (
    <Box sx={{ mt: 4, mb: 5 }} position="relative">
      <Autocomplete
        value={state}
        onChange={(_, newValue) => {
          setState(newValue)
          setSchool(newValue?.id ?? null)
        }}
        loading={isLoading}
        loadingText="Loading..."
        slotProps={{
          paper: {
            sx: {
              border: `1px solid ${theme.palette.mockup.neutral40}`,
              borderTopLeftRadius: 0,
              borderTopRightRadius: 0,
              borderBottomLeftRadius: "16px",
              borderBottomRightRadius: "16px",
              boxShadow: "none",
              marginTop: "-2px",
            },
          },
          listbox: {
            sx: {
              maxHeight: "40vh",
              borderTopRightRadius: 0,
              borderTopLeftRadius: 0,
            },
          },
        }}
        renderInput={(params) => (
          <TextFieldStyle
            {...params}
            label={t("noAuthFlow.searchSchool")}
            invalid={!!errorInfo}
            error={!!errorInfo}
            inputProps={{
              ...params.inputProps,
              "aria-invalid": !!errorInfo,
              ...(errorInfo && { "aria-describedby": errorId }),
            }}
          />
        )}
        isOptionEqualToValue={(option, value) => option.name === value.name}
        filterOptions={(options) => options}
        noOptionsText={
          userPost?.length === 0 && data.length === 0 ? (
            <Box sx={{ textAlign: "center" }}>
              {t("noAuthFlow.startTypingtoFindTSchool")}...
            </Box>
          ) : userPost && userPost.length > 0 && data.length === 0 ? (
            <Box
              component="button"
              type="button"
              onClick={() => dispatch(setActivePageSignUp(4))}
              sx={{
                ...unstyledButtonSx,
                display: "block",
                width: "100%",
                textAlign: "center",
                border: "3px solid transparent",
                textTransform: "uppercase",
                color: `${theme.palette.primary.main}`,
                cursor: "pointer",
                fontSize: "inherit",
                boxShadow: "0px 0px 0px rgba(210, 218, 249, 0.25)",
              }}
            >
              {t("noAuthFlow.dontSeeYourSchoolAddItHere")}
            </Box>
          ) : (
            ""
          )
        }
        options={data}
        getOptionLabel={(option: IAutocompleteProps) => `${option.name}`}
        onInputChange={debounce((_, val, reason) => {
          if (val) {
            setSkip(0)
          }
          setUserPost(val)
          if (reason === "reset") {
            setSkip(0)
          }
        }, 500)}
        renderOption={(props, originalOption, { index }) => {
          const option = originalOption as IAutocompleteProps
          return (
            <React.Fragment key={option.id}>
              <li
                data-option={JSON.stringify(option)}
                data-val={option.id}
                {...props}
                style={{
                  position: "relative",
                  background: theme.palette.common.white,
                }}
              >
                <Box
                  display="flex"
                  justifyContent="space-between"
                  width="100%"
                  alignItems="center"
                >
                  <Stack>
                    <Tooltip
                      title={option.name}
                      placement="top"
                      disableHoverListener={!isTextTruncated(option.name)}
                    >
                      <Typography
                        sx={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          maxWidth: "250px",
                        }}
                      >
                        {option.name}
                      </Typography>
                    </Tooltip>
                    <Tooltip
                      title={option.street_address}
                      placement="top"
                      disableHoverListener={
                        !option.street_address ||
                        !isTextTruncated(option.street_address)
                      }
                    >
                      <Typography
                        variant="body2"
                        color="mockup.neutral60"
                        sx={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          maxWidth: "250px",
                        }}
                      >
                        {option.street_address || "-"}
                      </Typography>
                    </Tooltip>
                    <Tooltip
                      title={`${option.district}${
                        option.zip_code ? `, ${option.zip_code}` : ""
                      }`}
                      placement="top"
                      disableHoverListener={
                        !isTextTruncated(
                          `${option.district}${
                            option.zip_code ? `, ${option.zip_code}` : ""
                          }`
                        )
                      }
                    >
                      <Typography
                        variant="body2"
                        color="mockup.neutral60"
                        sx={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          maxWidth: "250px",
                        }}
                      >
                        {option.district}
                        {option.zip_code ? `, ${option.zip_code}` : ""}
                      </Typography>
                    </Tooltip>
                  </Stack>
                  <Typography
                    variant="body2"
                    color="mockup.neutral60"
                    sx={{
                      whiteSpace: "nowrap",
                      textAlign: "left",
                      minWidth: "80px",
                    }}
                  >
                    {option.teachers_count}{" "}
                    {option.teachers_count === 1 ? "Teacher" : "Teachers"}
                  </Typography>
                </Box>
              </li>
              {index === data.length - 1 && (
                <Box>
                  {skip > 0 || data?.length === 20 ? (
                    <Box display="flex" justifyContent="space-around">
                      <Button
                        style={{ height: "10px" }}
                        onClick={onClickPrevSkip}
                        disabled={skip === 0}
                      >
                        <Arrow />
                      </Button>
                      <Button
                        disabled={data.length !== 20}
                        onClick={onClickNextSkip}
                        style={{ transform: "rotate(180deg)", height: "10px" }}
                      >
                        <Arrow />
                      </Button>
                    </Box>
                  ) : null}
                  <DropdownStyles
                    type="button"
                    onClick={() => dispatch(setActivePageSignUp(4))}
                  >
                    {t("noAuthFlow.dontSeeYourSchoolAddItHere")}
                  </DropdownStyles>
                </Box>
              )}
            </React.Fragment>
          )
        }}
      />
      {errorInfo && (
        <ErrorStyle id={errorId} role="alert">
          {errorInfo}
        </ErrorStyle>
      )}
    </Box>
  )
}

export default AutocompleteInput
